import { ImageFileCard } from '@/components/ui/attachments/ImageFileCard';
import { LinkedContextBadge } from '@/components/ui/linked-context-badge';
import { useComposerShortcutsFlyout } from '@/components/ui/session-ask-heidi/use-in-session-shortcut-flyout';
import { useIsChatObjectRefsEnabled } from '@/features/flags';
import { useDesktopTargetSelection } from '@/features/rpa/desktop-target/useDesktopTargetSelection';
import { EvidenceRegulatoryFootnoteWithIfu } from '@/features/settings/account/components/evidence-regulatory-footnote-with-ifu';
import { VoiceAssistantTrigger } from '@/features/voice-assistant';
import { getPreviewUrl } from '@/hooks/useChatFileUpload';
import { useChatPaste } from '@/hooks/useChatPaste';
import {
  WorkSessionComposer as WorkSessionComposerView,
  type ComposerAttachments,
  type ProjectsComposerHandle,
  type ProjectsComposerPromptEditorHandle,
  type WorkComposerSessionAppearance,
  type WorkSessionComposerHost,
  type WorkSessionComposerProps,
} from '@heidi/agents';
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { useProjectsComposerChatSession } from '../hooks/useProjectsComposerChatSession';
import { InlineClarificationBlock } from './InlineClarificationBlock';
import { ProjectsLandingComposer } from './ProjectsLandingComposer';

export type ProjectChatComposerHandle = ProjectsComposerHandle;

const DEFAULT_SESSION_APPEARANCE: WorkComposerSessionAppearance = { kind: 'default' };

// Module-level so the composer sees one stable host for the life of the app. The
// three factory ports are read when the composer renders, as they always were.
const WORK_SESSION_COMPOSER_HOST: WorkSessionComposerHost = {
  ClarificationBlock: InlineClarificationBlock,
  LinkedContextBadge,
  RegulatoryFootnote: EvidenceRegulatoryFootnoteWithIfu,
  VoiceTrigger: VoiceAssistantTrigger,
  LinkContext: (options) => ProjectsLandingComposer.LinkContext(options),
  DictateSlot: (options) => ProjectsLandingComposer.DictateSlot(options),
  // Bound directly, not a factory: DictationBar takes no options, so a
  // factory would have nothing to close over.
  DictationBar: ProjectsLandingComposer.DictationBar(),
  PromptEditor: (options) => ProjectsLandingComposer.PromptEditor(options),
};

/**
 * Binds the Work chat composer to what the app owns: the session over the app's
 * inputs, the desktop target, the clipboard paste, the object-refs flag, Chat's
 * image chips and the in-session shortcuts flyout.
 */
const WorkSessionComposer = forwardRef<ProjectsComposerHandle, WorkSessionComposerProps>(
  function WorkSessionComposer(props, ref) {
    const isSubmitting = props.isSubmitting ?? false;
    const sessionAppearance = props.sessionAppearance ?? DEFAULT_SESSION_APPEARANCE;
    const isFocusedAppearance = sessionAppearance.kind === 'focused';
    const contextLocked = props.contextLock != null;
    const canLinkPatientSessions = Boolean(props.patientScope?.patientProfileId);
    const editorRef = useRef<ProjectsComposerPromptEditorHandle | null>(null);
    const desktopTarget = useDesktopTargetSelection({
      surface: sessionAppearance.kind === 'landing' ? 'landing' : 'project',
      desktopTargetEnabled:
        props.desktopTargetEnabled && !isFocusedAppearance && !contextLocked,
    });

    const chat = useProjectsComposerChatSession({
      ...props,
      isSubmitting,
      editorRef,
      idlePlaceholder:
        sessionAppearance.kind === 'focused' || sessionAppearance.kind === 'default'
          ? sessionAppearance.idlePlaceholder
          : undefined,
      onComposerEmptyChange: props.onSessionDraftEmptyChange,
      idlePlaceholderPreview:
        props.placeholderPreview ??
        (isFocusedAppearance ? sessionAppearance.idlePlaceholderPreview : undefined),
      lockedSessionPatientScope: canLinkPatientSessions ? props.patientScope : null,
    });
    const isComposerLocked = props.runClosed || Boolean(props.readOnly);
    useImperativeHandle(ref, () => chat.handle, [chat.handle]);
    const chatObjectRefsEnabled = useIsChatObjectRefsEnabled();
    const shortcutsFlyout = useComposerShortcutsFlyout();

    const { handlePaste } = useChatPaste({
      onFilesDetected: chat.onFilesPicked,
      enabled: !isComposerLocked,
    });

    const attachments = useMemo<ComposerAttachments>(
      () => ({
        ...chat.attachments,
        renderImageItem: (item, index) => {
          const file = chat.preUploadedFiles[index];
          if (!file) return null;
          return (
            <ImageFileCard
              file={file.file}
              uploadStatus={file.uploadStatus}
              uploadProgress={item.progress}
              previewUrl={getPreviewUrl(file)}
              errorMessage={item.error}
              onDelete={() => chat.attachments.remove(item.id)}
              onRetry={
                item.status === 'error'
                  ? () => chat.attachments.retry?.(item.id)
                  : undefined
              }
            />
          );
        },
      }),
      [chat.attachments, chat.preUploadedFiles]
    );

    return (
      <WorkSessionComposerView
        {...props}
        chat={chat}
        editorRef={editorRef}
        desktopTarget={desktopTarget}
        attachments={attachments}
        onClipboardPaste={handlePaste}
        chatObjectRefsEnabled={chatObjectRefsEnabled}
        shortcutsFlyout={shortcutsFlyout}
        host={WORK_SESSION_COMPOSER_HOST}
      />
    );
  }
);

export const ProjectChatComposer = memo(WorkSessionComposer);
